<template>
  <div class="wrapper-tasks">
    <div class="tasks">
      <div class="taskslist">
        <div class="items">
          <template v-if="taskIds">
            <div class="tasks-container">
              <Drop
                ref="todayDropListEl"
                :accepts-type="['allocation', 'task']"
                class="list"
                :class="{ dragOver: hoverState === 'today' }"
                @dragover="
                  (e: DraggedAllocation | DraggedTask) => onDragOver(e, 'today')
                "
                @dragleave="() => onDragLeave('today')"
                @drop="
                  (e: DraggedAllocation | DraggedTask) => onDrop(e, 'today')
                "
                @dragend="() => onDragEnd()"
              >
                <CollapsibleTaskList
                  ref="taskListTodayEl"
                  :date="date"
                  :is-today="today"
                  :items="agendaTasks?.current || []"
                  :can-add-task="!isInPast"
                  :title="format(date, 'EEEE')"
                  :is-collapsed="sections.today.state.value === 'collapsed'"
                  :hide-scheduled-date="true"
                  :list-key="dayListKey"
                  @update:is-collapsed="updateCollapsedState('today', $event)"
                  @unselect-items="$emit('unselectItems')"
                  @is-active="(active) => activateSection('today', active)"
                />
              </Drop>
              <template v-if="!isInPast">
                <!--
                  Unlike the other fixed sections, the Week section is always
                  rendered (even empty, like the Calendar sidebar's): it is
                  the drop target for week-scheduling a task.
                -->
                <Drop
                  ref="weekDropListEl"
                  :accepts-type="['allocation', 'task']"
                  :accepts-data="acceptsWeekPlannable"
                  class="list"
                  :class="{ dragOver: hoverState === 'week' }"
                  @dragover="
                    (e: DraggedAllocation | DraggedTask) =>
                      onDragOver(e, 'week')
                  "
                  @dragleave="() => onDragLeave('week')"
                  @drop="
                    (e: DraggedAllocation | DraggedTask) => onDrop(e, 'week')
                  "
                  @dragend="() => onDragEnd()"
                >
                  <CollapsibleTaskList
                    ref="taskListWeekEl"
                    :date="date"
                    :is-today="today"
                    :items="weekTaskIds"
                    :can-add-task="true"
                    :add-task-action="weekAddTaskAction"
                    :title="weekTitle"
                    :is-collapsed="sections.week.state.value === 'collapsed'"
                    :list-key="weekListKey"
                    @update:is-collapsed="updateCollapsedState('week', $event)"
                    @unselect-items="$emit('unselectItems')"
                    @is-active="(active) => activateSection('week', active)"
                  />
                </Drop>
                <CollapsibleTaskList
                  v-if="overdueTaskIds.length > 0"
                  ref="taskListOverdueEl"
                  :date="date"
                  :is-today="today"
                  :items="overdueTaskIds"
                  :title="'Overdue'"
                  :is-overdue="true"
                  :is-collapsed="sections.overdue.state.value === 'collapsed'"
                  @update:is-collapsed="updateCollapsedState('overdue', $event)"
                  @unselect-items="$emit('unselectItems')"
                  @is-active="(active) => activateSection('overdue', active)"
                />
                <Drop
                  v-if="upcomingTaskIds.length > 0"
                  ref="upcomingDropListEl"
                  :accepts-type="['allocation', 'task']"
                  :accepts-data="acceptsWeekPlannable"
                  class="list"
                  :class="{ dragOver: hoverState === 'upcoming' }"
                  @dragover="
                    (e: DraggedAllocation | DraggedTask) =>
                      onDragOver(e, 'upcoming')
                  "
                  @dragleave="() => onDragLeave('upcoming')"
                  @drop="
                    (e: DraggedAllocation | DraggedTask) =>
                      onDrop(e, 'upcoming')
                  "
                  @dragend="() => onDragEnd()"
                >
                  <CollapsibleTaskList
                    ref="taskListUpcomingEl"
                    :date="date"
                    :is-today="today"
                    :items="upcomingTaskIds"
                    :title="'Upcoming'"
                    :is-collapsed="
                      sections.upcoming.state.value === 'collapsed'
                    "
                    @update:is-collapsed="
                      updateCollapsedState('upcoming', $event)
                    "
                    @unselect-items="$emit('unselectItems')"
                    @is-active="(active) => activateSection('upcoming', active)"
                  />
                </Drop>
                <Drop
                  v-if="unplannedTaskIds.length > 0"
                  ref="unplannedDropListEl"
                  :accepts-type="['allocation', 'task']"
                  class="list"
                  :class="{ dragOver: hoverState === 'unplanned' }"
                  @dragover="
                    (e: DraggedAllocation | DraggedTask) =>
                      onDragOver(e, 'unplanned')
                  "
                  @dragleave="() => onDragLeave('unplanned')"
                  @drop="
                    (e: DraggedAllocation | DraggedTask) =>
                      onDrop(e, 'unplanned')
                  "
                  @dragend="() => onDragEnd()"
                >
                  <CollapsibleTaskList
                    ref="taskListUnplannedEl"
                    :date="date"
                    :is-today="today"
                    :items="unplannedTaskIds"
                    :title="'Unplanned'"
                    :is-collapsed="
                      sections.unplanned.state.value === 'collapsed'
                    "
                    :pagination="20"
                    :list-key="unplannedListKey"
                    @update:is-collapsed="
                      updateCollapsedState('unplanned', $event)
                    "
                    @unselect-items="$emit('unselectItems')"
                    @is-active="
                      (active) => activateSection('unplanned', active)
                    "
                  />
                </Drop>
              </template>
              <Drop
                v-if="(agendaTasks?.completed || []).length > 0"
                ref="completedDropListEl"
                :accepts-type="['allocation', 'task']"
                class="list"
                :class="{ dragOver: hoverState === 'completed' }"
                @dragover="
                  (e: DraggedAllocation | DraggedTask) =>
                    onDragOver(e, 'completed')
                "
                @dragleave="() => onDragLeave('completed')"
                @drop="
                  (e: DraggedAllocation | DraggedTask) => onDrop(e, 'completed')
                "
                @dragend="() => onDragEnd()"
              >
                <CollapsibleTaskList
                  ref="taskListCompletedEl"
                  :date="date"
                  :is-today="today"
                  :empty-text="'No completed tasks.'"
                  :items="agendaTasks?.completed || []"
                  title="Completed"
                  :is-collapsed="sections.completed.state.value === 'collapsed'"
                  :is-collapsable="!isInPast"
                  @update:is-collapsed="
                    updateCollapsedState('completed', $event)
                  "
                  @unselect-items="$emit('unselectItems')"
                  @is-active="(active) => activateSection('completed', active)"
                />
              </Drop>
              <Drop
                v-if="(agendaTasks?.ignored || []).length > 0"
                ref="discardedDropListEl"
                :accepts-type="['allocation', 'task']"
                class="list"
                :class="{ dragOver: hoverState === 'discarded' }"
                @dragover="
                  (e: DraggedAllocation | DraggedTask) =>
                    onDragOver(e, 'discarded')
                "
                @dragleave="() => onDragLeave('discarded')"
                @drop="
                  (e: DraggedAllocation | DraggedTask) => onDrop(e, 'discarded')
                "
                @dragend="() => onDragEnd()"
              >
                <CollapsibleTaskList
                  ref="taskListDiscardedEl"
                  :date="date"
                  :is-today="today"
                  :empty-text="'No discarded tasks.'"
                  :items="agendaTasks?.ignored || []"
                  title="Discarded"
                  :is-collapsed="sections.discarded.state.value === 'collapsed'"
                  :is-collapsable="!isInPast"
                  @update:is-collapsed="
                    updateCollapsedState('discarded', $event)
                  "
                  @unselect-items="$emit('unselectItems')"
                  @is-active="(active) => activateSection('discarded', active)"
                />
              </Drop>
            </div>
          </template>
          <Loader v-else>
            <BaseSkeletonList />
          </Loader>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { addWeeks, format, isAfter, isToday, startOfDay } from "date-fns";
import {
  computed,
  ComputedRef,
  defineComponent,
  nextTick,
  Ref,
  ref,
  toRefs,
} from "vue";
import { Drop } from "vue-easy-dnd";

import controller from "@ctrl/controller";
import type { ConsoleActionTask } from "@ctrl/controller-rpc";

import assert from "@/assert";
import CollapsibleTaskList from "@/components/agenda/CollapsibleTaskList.vue";
import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import * as inject from "@/components/inject";
import Loader from "@/components/Loader.vue";
import {
  dayKey,
  personalSectionKey,
  taskListKeys,
  weekKey,
} from "@/components/tasks/ordering";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { bindAsync, useReleaser } from "@/ref";
import { DraggedAllocation, DraggedTask } from "@/types";

import {
  acceptsWeekPlannable,
  dropPlannableOnSection,
  type DroppableTaskSection,
} from "./dnd";

type SectionState = "collapsed" | "expanded";

type AllSections = DroppableTaskSection | "overdue";
type DroppableSections = DroppableTaskSection;

type Section = {
  state: Ref<SectionState>;
  el: Ref<InstanceType<typeof CollapsibleTaskList> | undefined>;
};

const { weekOfDate } = controller;

export default defineComponent({
  name: "Tasks",
  components: {
    BaseSkeletonList,
    Loader,
    CollapsibleTaskList,
    Drop,
  },
  props: {
    date: { type: Date, required: true },
  },
  emits: {
    unselectItems: () => true,
  },
  async setup(props) {
    const { date } = toRefs(props);
    const controller = inject.controller();
    const clock = inject.clock();
    const todayDate = inject.today();

    const hoverState: Ref<DroppableSections | null> = ref(null);
    const todayDropListEl = ref<InstanceType<typeof Drop>>();
    const weekDropListEl = ref<InstanceType<typeof Drop>>();
    const upcomingDropListEl = ref<InstanceType<typeof Drop>>();
    const unplannedDropListEl = ref<InstanceType<typeof Drop>>();
    const completedDropListEl = ref<InstanceType<typeof Drop>>();

    // Load & sync sections states with the browser's local storage.
    const profile = inject.profile();
    const releaser = useReleaser();
    const { syncState } = useBrowserStore(profile.value.user.accountId);
    const todayState = releaser.add(
      syncState<SectionState>("agenda/sections/today", "expanded")
    );
    const weekState = releaser.add(
      syncState<SectionState>("agenda/sections/week", "collapsed")
    );
    const overdueState = releaser.add(
      syncState<SectionState>("agenda/sections/overdue", "collapsed")
    );
    const upcomingState = releaser.add(
      syncState<SectionState>("agenda/sections/upcoming", "collapsed")
    );
    const unplannedState = releaser.add(
      syncState<SectionState>("agenda/sections/unplanned", "collapsed")
    );
    const completedState = releaser.add(
      syncState<SectionState>("agenda/sections/completed", "collapsed")
    );
    const discardedState = releaser.add(
      syncState<SectionState>("agenda/sections/discarded", "collapsed")
    );

    // Template refs for each section
    const taskListTodayEl = ref<InstanceType<typeof CollapsibleTaskList>>();
    const taskListWeekEl = ref<InstanceType<typeof CollapsibleTaskList>>();
    const taskListOverdueEl = ref<InstanceType<typeof CollapsibleTaskList>>();
    const taskListUpcomingEl = ref<InstanceType<typeof CollapsibleTaskList>>();
    const taskListUnplannedEl = ref<InstanceType<typeof CollapsibleTaskList>>();
    const taskListCompletedEl = ref<InstanceType<typeof CollapsibleTaskList>>();
    const taskListDiscardedEl = ref<InstanceType<typeof CollapsibleTaskList>>();

    const sections: ComputedRef<Record<AllSections, Section>> = computed(
      () => ({
        today: { state: todayState, el: taskListTodayEl },
        week: { state: weekState, el: taskListWeekEl },
        overdue: { state: overdueState, el: taskListOverdueEl },
        upcoming: { state: upcomingState, el: taskListUpcomingEl },
        unplanned: { state: unplannedState, el: taskListUnplannedEl },
        completed: { state: completedState, el: taskListCompletedEl },
        discarded: { state: discardedState, el: taskListDiscardedEl },
      })
    );
    const activeSection = ref<AllSections | null>(null);

    const activateSection = (sectionName: AllSections, active: boolean) => {
      if (active) {
        // If we are setting a section to active (at least one task selected),
        // make sure the currently active section is deactivated i.e. all tasks
        // are deselected.
        if (activeSection.value !== sectionName) {
          unselectTask();
        }
        // Set the new active section.
        activeSection.value = sectionName;
      } else {
        // Out of precaution, unselect the task from the given section.
        unselectTask(sectionName);
        // If the given section is the active one, that means there is no more
        // any active section.
        if (activeSection.value === sectionName) {
          activeSection.value = null;
        }
      }
    };

    const updateCollapsedState = (id: AllSections, value: boolean) => {
      const newState = value ? "collapsed" : "expanded";
      sections.value[id].state.value = newState;

      // If the section is collapsing, deactive the section for items
      // to be selected.
      if (newState === "collapsed") {
        activateSection(id, false);
      }
    };

    const unselectTask = (
      sectionName: AllSections | null = activeSection.value
    ) => {
      if (!sectionName) return;
      sections.value[sectionName].el.value?.unselectTask();
    };

    const focusAddTask = async () => {
      // The "Add task..." input only exists while the "today" list is
      // expanded, so expand it first if needed.
      if (todayState.value === "collapsed") {
        updateCollapsedState("today", false);
        await nextTick();
      }
      // Focus the "Add task..." input in the "today" list (if available).
      sections.value.today.el.value?.focusAddTask?.();
    };

    const isInPast = computed(() =>
      isAfter(startOfDay(clock.value), startOfDay(date.value))
    );

    const agendaTasks = await releaser.add(
      bindAsync(date, async (d) => await controller.agendaTasks(d))
    );
    // The planner's calendarTasks batch, anchored to today (not the browsed
    // day), feeds every section that isn't the day's own record: Week N
    // (`postponed` — agendaTasks has no week group), Overdue, Upcoming and
    // Unplanned. Like the Planner sidebar (same RPC, same anchor), those
    // lists are computed from today's date, so navigating days only changes
    // the day's lists (day / Completed / Discarded, from agendaTasks above).
    const calendarTasks = await releaser.add(
      bindAsync(todayDate, async (d) => await controller.calendarTasks(d))
    );
    const weekTaskIds = computed(() => calendarTasks.value?.postponed ?? []);
    const overdueTaskIds = computed(() => calendarTasks.value?.overdue ?? []);
    const upcomingTaskIds = computed(() => calendarTasks.value?.upcoming ?? []);
    const unplannedTaskIds = computed(
      () => calendarTasks.value?.unplanned ?? []
    );
    const week = computed(() => weekOfDate(todayDate.value));
    const weekTitle = computed(() => `Week ${week.value.n}`);
    // "Add task…" in the Week section plans the task for the week it
    // displays (today's week), like the Calendar sidebar's week list.
    const weekAddTaskAction = computed<ConsoleActionTask>(() => ({
      type: "task-postponing",
      batch: week.value,
    }));
    const dayListKey = computed(() =>
      personalSectionKey(profile.value, dayKey(date.value))
    );
    // Same key as the Planner sidebar week so both share one ranking.
    const weekListKey = computed(() =>
      personalSectionKey(profile.value, weekKey(week.value))
    );
    const unplannedListKey = computed(() =>
      personalSectionKey(profile.value, taskListKeys.unplanned)
    );

    const visibleIdsForSection = (
      section: AllSections
    ): string[] | undefined => {
      const tasks = agendaTasks.value;
      if (!tasks) return undefined;
      switch (section) {
        case "today":
          return tasks.current;
        case "week":
          return weekTaskIds.value;
        case "overdue":
          return overdueTaskIds.value;
        case "unplanned":
          return unplannedTaskIds.value;
        case "upcoming":
          return upcomingTaskIds.value;
        case "completed":
          return tasks.completed;
        case "discarded":
          return tasks.ignored;
        default:
          return undefined;
      }
    };

    /** Row-drag drop on the section the task already belongs to — no-op
     * (reorder is handle-only via Ordering). */
    const isDropInSameSection = (
      e: DraggedTask,
      section: DroppableSections
    ): boolean => {
      const visible = visibleIdsForSection(section);
      return !!visible?.includes(e.data.task.id);
    };
    const taskIds = computed<string[] | null>(() =>
      agendaTasks?.value?.current && agendaTasks?.value?.completed
        ? [...agendaTasks.value.current, ...agendaTasks.value.completed]
        : null
    );

    const today = computed(() => {
      // The assert allows for today to be recomputed when we change day.
      assert(clock.value);
      return isToday(date.value);
    });

    const onDragOver = (
      e: DraggedAllocation | DraggedTask,
      section: DroppableSections
    ) => {
      // `accepts-data` refuses the drop itself, but vue-easy-dnd still fires
      // `dragover`: don't highlight a section that will not take the payload.
      if (
        (section === "week" || section === "upcoming") &&
        !acceptsWeekPlannable(e.data)
      )
        return;
      hoverState.value = section;
    };

    const onDragLeave = (_section: DroppableSections) => {
      hoverState.value = null;
    };

    const onDrop = (
      e: DraggedAllocation | DraggedTask,
      section: DroppableSections
    ) => {
      if (!e.success) return;
      // Handle reorder is Ordering-only; same-section row drop is a no-op.
      if (e.type === "task" && isDropInSameSection(e, section)) {
        hoverState.value = null;
        return;
      }
      dropPlannableOnSection(controller, e, section, {
        date: date.value,
        week: week.value,
        nextWeek: weekOfDate(addWeeks(new Date(), 1)),
      });
      hoverState.value = null;
    };

    const onDragEnd = () => {
      hoverState.value = null;
    };

    return {
      agendaTasks,
      activeSection,
      sections,
      hoverState,
      format,
      isInPast,
      taskIds,
      weekTaskIds,
      weekAddTaskAction,
      overdueTaskIds,
      upcomingTaskIds,
      unplannedTaskIds,
      weekTitle,
      acceptsWeekPlannable,
      taskListTodayEl,
      taskListWeekEl,
      taskListOverdueEl,
      taskListUpcomingEl,
      taskListUnplannedEl,
      taskListCompletedEl,
      todayDropListEl,
      weekDropListEl,
      upcomingDropListEl,
      unplannedDropListEl,
      completedDropListEl,
      dayListKey,
      weekListKey,
      unplannedListKey,
      today,
      activateSection,
      updateCollapsedState,
      onDragOver,
      onDragLeave,
      onDrop,
      onDragEnd,
      unselectTask,
      focusAddTask,
    };
  },
});
</script>

<style scoped lang="scss">
.wrapper-tasks {
  width: 100%;
  padding-top: 8px;

  .tasks {
    width: 100%;

    .base-skeleton-list {
      margin-inline-start: 32px;
      margin-top: 16px;
    }

    .todayTasks {
      margin-top: -1px;
    }

    hr {
      margin-top: 24px;
      visibility: hidden;
    }

    .taskslist {
      padding: 0;
      width: 100%;

      .tasks-container {
        padding-bottom: 24px;
      }
    }

    .list {
      transition: background-color 0.2s ease-out;
      border-radius: $border-radius-l;
      border: 1px solid transparent;
      @include transition(border 0.2s ease-out);

      &.dragOver {
        @include themed() {
          border: 0.5px dashed t($secondary-50);
        }
      }
    }

    .drop-placeholder {
      height: 2px;
      width: 100%;

      @include themed() {
        background-color: t($secondary);
      }
    }

    .title {
      @extend %font-caption-regular;
      text-transform: uppercase;
      margin: 0;
      margin-inline-start: 20px;
      margin-bottom: 8px;
      @include themed() {
        color: t($grey-50);
      }
    }
  }
}
</style>
